// Visible Rename / Change Icon / Delete actions for the currently selected
// bookmark folder. Used as an inline bar (mobile, under the folder pills) and
// in a compact icon-only variant (the folder rail).

import React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { Tooltip } from '@/components/Tooltip';

const ActionButton = ({ icon, label, onPress, destructive, compact, colors }) => {
  const tint = destructive ? colors.color_destructive : colors.text_color_primary;
  const button = (
    <Pressable
      onPress={onPress}
      hitSlop={6}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ hovered, pressed }) => [
        compact ? styles.btnCompact : styles.btn,
        {
          borderColor: colors.border_color_secondary,
          backgroundColor: colors.solid_color_block,
        },
        (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
        Platform.OS === 'web' && { cursor: 'pointer' },
      ]}
    >
      <Icon name={icon} size={compact ? 15 : 14} color={tint} />
      {compact ? null : (
        <Text size="s" weight="medium" style={{ color: tint }}>
          {label}
        </Text>
      )}
    </Pressable>
  );

  if (!compact) return button;
  return (
    <Tooltip label={label} side="bottom">
      {button}
    </Tooltip>
  );
};

ActionButton.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onPress: PropTypes.func.isRequired,
  destructive: PropTypes.bool,
  compact: PropTypes.bool,
  colors: PropTypes.object.isRequired,
};

function BookmarkFolderActions({ folder, onRename, onChangeIcon, onDelete, onCreateSubfolder, onMove, t, compact, style }) {
  const { colors } = useTheme();
  if (!folder) return null;

  return (
    <View style={[styles.bar, style]}>
      {onCreateSubfolder ? (
        <ActionButton
          icon="folder-plus"
          label={t('pages.bookmarks.newSubfolder')}
          onPress={() => onCreateSubfolder(folder)}
          compact={compact}
          colors={colors}
        />
      ) : null}
      <ActionButton
        icon="pen"
        label={t('dropdowns.rename')}
        onPress={() => onRename(folder)}
        compact={compact}
        colors={colors}
      />
      <ActionButton
        icon="grid-2"
        label={t('pages.bookmarks.changeIcon')}
        onPress={() => onChangeIcon(folder)}
        compact={compact}
        colors={colors}
      />
      {onMove ? (
        <ActionButton
          icon="folder-arrow-down"
          label={t('pages.bookmarks.moveFolder')}
          onPress={() => onMove(folder)}
          compact={compact}
          colors={colors}
        />
      ) : null}
      <ActionButton
        icon="trash"
        label={t('common.delete')}
        onPress={() => onDelete(folder)}
        destructive
        compact={compact}
        colors={colors}
      />
    </View>
  );
}

BookmarkFolderActions.propTypes = {
  folder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    icon: PropTypes.string,
  }),
  onRename: PropTypes.func.isRequired,
  onChangeIcon: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCreateSubfolder: PropTypes.func,
  onMove: PropTypes.func,
  t: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  style: PropTypes.any,
};

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
  },
  btnCompact: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    borderWidth: 1,
  },
});

export default BookmarkFolderActions;
